import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for debouncing a value
 * @param {any} value - The value to debounce
 * @param {number} delay - Delay in milliseconds (default: 500ms)
 * @returns {any} - Debounced value
 */
export function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook for debounced callback function
 * @param {Function} callback - The callback function to debounce
 * @param {number} delay - Delay in milliseconds (default: 500ms)
 * @returns {Function} - Debounced callback function
 */
export function useDebouncedCallback(callback, delay = 500) {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

/**
 * Custom hook for server-side search with debouncing
 * @param {Function} searchFn - The search function to call
 * @param {number} delay - Delay in milliseconds (default: 500ms)
 * @returns {Object} - { searchQuery, setSearchQuery, isSearching, debouncedQuery }
 */
export function useServerSearch(searchFn, delay = 500) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, delay);
  const previousQueryRef = useRef('');

  useEffect(() => {
    // Only search if query changed
    if (debouncedQuery !== previousQueryRef.current) {
      previousQueryRef.current = debouncedQuery;

      if (searchFn) {
        setIsSearching(true);
        Promise.resolve(searchFn(debouncedQuery))
          .finally(() => {
            setIsSearching(false);
          });
      }
    }
  }, [debouncedQuery, searchFn]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    isSearching,
    debouncedQuery,
    clearSearch,
  };
}

export default useDebounce;
